import React from 'react';
import { UserModel } from './useUserManagement';
import UserProStatus from './UserProStatus';
import { effectiveEntitlement } from './entitlementState';

const UserDetailPanel: React.FC<{ model: UserModel }> = ({ model }) => {
  const {
    selectedUser, detailLoading, sendingReset, riskProfile,
    setSuspendModal, setSuspendDuration, setDeleteModal, setDeleteConfirmEmail,
    setDeleteReason, handleUnsuspend, handleSendPasswordReset,
    isSuspended, formatDate, formatDateTime,
  } = model;

  if (!selectedUser && !detailLoading) {
    return (
      <div className="rp-bento-card text-center py-12">
        <p style={{ color: 'var(--rp-text-tertiary)' }}>Select a user to view details</p>
      </div>
    );
  }

  if (!selectedUser) {
    return detailLoading ? (
      <div className="rp-bento-card text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-4" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }} />
        <p style={{ color: 'var(--rp-text-secondary)' }}>Loading user details...</p>
      </div>
    ) : null;
  }

  return (
    <div style={{ position: 'relative' }}>
      {detailLoading && (
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 10, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animate-spin w-6 h-6 border-2 rounded-full" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }} />
        </div>
      )}
      <div className="space-y-4">
        {/* User Header */}
        <div className="rp-bento-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
                style={{
                  backgroundColor: isSuspended(selectedUser.banned_until) ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)',
                  color: isSuspended(selectedUser.banned_until) ? 'var(--admin-error)' : 'var(--rp-primary)',
                }}
              >
                {(selectedUser.profile.display_name || selectedUser.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--rp-text)' }}>
                  {selectedUser.profile.display_name || 'No display name'}
                </h2>
                <p className="text-sm" style={{ color: 'var(--rp-text-secondary)' }}>{selectedUser.email}</p>
                {selectedUser.profile.username && (
                  <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>@{selectedUser.profile.username}</p>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {isSuspended(selectedUser.banned_until) && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400 border border-red-400/30">
                      SUSPENDED until {formatDate(selectedUser.banned_until)}
                    </span>
                  )}
                  {!selectedUser.status_stale && effectiveEntitlement(
                    selectedUser.profile.premium_override,
                    selectedUser.profile.premium_override_expires_at,
                    selectedUser.entitlement
                      ? {
                          entitlement_tier: selectedUser.entitlement.tier,
                          entitlement_status: selectedUser.entitlement.status,
                          entitlement_platform: selectedUser.entitlement.platform,
                          entitlement_period_end: selectedUser.entitlement.period_end,
                        }
                      : null,
                  ).kind !== 'free' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-400/30">PRO</span>
                  )}
                  {selectedUser.email_confirmed_at && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-400 border border-green-400/30">VERIFIED</span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              {isSuspended(selectedUser.banned_until) ? (
                <button onClick={() => handleUnsuspend(selectedUser.id, selectedUser.email)}
                  className="admin-btn-secondary px-3 py-2 rounded-lg text-xs font-semibold"
                  style={{ color: 'var(--admin-success)', borderColor: 'rgba(16,185,129,0.3)' }}
                >
                  Unsuspend
                </button>
              ) : (
                <button onClick={() => { setSuspendModal({ userId: selectedUser.id, email: selectedUser.email }); setSuspendDuration('7d'); }}
                  className="admin-btn-secondary px-3 py-2 rounded-lg text-xs font-semibold"
                  style={{ borderColor: 'var(--admin-warning-strong)' }}
                >
                  Suspend
                </button>
              )}
              <button onClick={() => handleSendPasswordReset(selectedUser.email)}
                disabled={sendingReset}
                className="admin-btn-secondary px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
                style={{ borderColor: 'var(--admin-primary)' }}
              >
                {sendingReset ? 'Sending...' : 'Send Password Reset'}
              </button>
              <button onClick={() => { setDeleteModal({ userId: selectedUser.id, email: selectedUser.email }); setDeleteConfirmEmail(''); setDeleteReason(''); }}
                className="admin-btn admin-btn-danger text-xs font-semibold"
              >
                Delete (GDPR)
              </button>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="rp-bento-card">
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text-secondary)' }}>Account Info</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Signed Up</span>
              <span style={{ color: 'var(--rp-text)' }}>{formatDate(selectedUser.created_at)}</span>
            </div>
            <div>
              <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Last Sign In</span>
              <span style={{ color: 'var(--rp-text)' }}>{formatDateTime(selectedUser.last_sign_in_at)}</span>
            </div>
            <div>
              <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Location</span>
              <span style={{ color: 'var(--rp-text)' }}>
                {[selectedUser.profile.city, selectedUser.profile.state].filter(Boolean).join(', ') || 'Not set'}
              </span>
            </div>
            <div>
              <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Onboarded</span>
              <span style={{ color: 'var(--rp-text)' }}>
                {selectedUser.profile.onboarding_completed_at ? formatDate(selectedUser.profile.onboarding_completed_at) : 'Not completed'}
              </span>
            </div>
            {selectedUser.profile.experience_level && (
              <div>
                <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Experience</span>
                <span style={{ color: 'var(--rp-text)' }}>{selectedUser.profile.experience_level}</span>
              </div>
            )}
            {selectedUser.profile.riding_style && (
              <div>
                <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Riding Style</span>
                <span style={{ color: 'var(--rp-text)' }}>{selectedUser.profile.riding_style}</span>
              </div>
            )}
            {selectedUser.profile.ride_frequency && (
              <div>
                <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Ride Frequency</span>
                <span style={{ color: 'var(--rp-text)' }}>{selectedUser.profile.ride_frequency}</span>
              </div>
            )}
            {selectedUser.profile.bio && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-xs block" style={{ color: 'var(--rp-text-tertiary)' }}>Bio</span>
                <span className="text-sm" style={{ color: 'var(--rp-text)' }}>{selectedUser.profile.bio}</span>
              </div>
            )}
          </div>
        </div>

        <UserProStatus model={model} user={selectedUser} />

        {/* Activity Stats */}
        <div className="rp-bento-card">
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text-secondary)' }}>Activity</h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { label: 'Events Hosted', value: selectedUser.stats.events_hosted, color: 'var(--rp-primary)' },
              { label: 'Events Attended', value: selectedUser.stats.events_attended, color: 'var(--admin-success)' },
              { label: 'Chat Messages', value: selectedUser.stats.chat_messages, color: 'var(--rp-primary)' },
              { label: 'Friends', value: selectedUser.stats.friends, color: 'var(--admin-warning)' },
              { label: 'Vehicles', value: selectedUser.stats.vehicles, color: 'var(--rp-secondary)' },
            ].map(s => (
              <div key={s.label} className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
                <div className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Indicators */}
        {riskProfile && (riskProfile.content_reports > 0 || riskProfile.chat_bans > 0 || riskProfile.no_shows > 0 || riskProfile.blocks_received > 0) && (
          <div className="rp-bento-card" style={{
            borderLeft: `3px solid ${(riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'var(--admin-error)' : (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'var(--admin-warning)' : '#6b7280'}`,
          }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>Risk Indicators</h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{
                backgroundColor: (riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'rgba(239,68,68,0.15)' : (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'rgba(245,158,11,0.15)' : 'rgba(107,114,128,0.15)',
                color: (riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'var(--admin-error)' : (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'var(--admin-warning)' : '#6b7280',
              }}>
                {(riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'HIGH RISK' : (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'MODERATE' : 'LOW'}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'Reports', value: riskProfile.content_reports, color: riskProfile.content_reports > 0 ? 'var(--admin-error)' : '#6b7280' },
                { label: 'Chat Bans', value: riskProfile.chat_bans, color: riskProfile.chat_bans > 0 ? 'var(--admin-error)' : '#6b7280' },
                { label: 'No-Shows', value: riskProfile.no_shows, color: riskProfile.no_shows > 0 ? 'var(--admin-warning)' : '#6b7280' },
                { label: 'Blocked By', value: riskProfile.blocks_received, color: riskProfile.blocks_received > 0 ? 'var(--admin-warning)' : '#6b7280' },
              ].map(s => (
                <div key={s.label} className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                  <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Events */}
        {(selectedUser.recent_events.length > 0 || selectedUser.recent_participation.length > 0) && (
          <div className="rp-bento-card">
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text-secondary)' }}>Recent Activity</h3>
            {selectedUser.recent_events.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--rp-text-tertiary)' }}>Hosted Events</h4>
                <div className="space-y-1">
                  {selectedUser.recent_events.map(e => (
                    <div key={e.id} className="flex justify-between items-center p-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                      <span style={{ color: 'var(--rp-text)' }}>{e.title}</span>
                      <div className="flex gap-2 items-center">
                        <span style={{ color: 'var(--rp-text-tertiary)' }}>{e.city}, {e.state}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                          backgroundColor: e.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(107,114,128,0.2)',
                          color: e.status === 'active' ? 'var(--admin-success)' : '#6b7280',
                        }}>{e.status}</span>
                        <span style={{ color: 'var(--rp-text-tertiary)' }}>{e.meetup_time ? formatDate(e.meetup_time) : 'No date'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedUser.recent_participation.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--rp-text-tertiary)' }}>Attended Events</h4>
                <div className="space-y-1">
                  {selectedUser.recent_participation.map(p => (
                    <div key={p.id} className="flex justify-between items-center p-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                      <span style={{ color: 'var(--rp-text)' }}>{p.title}</span>
                      <div className="flex gap-2 items-center">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                          backgroundColor: p.checked_in_at ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)',
                          color: p.checked_in_at ? 'var(--admin-success)' : 'var(--admin-warning)',
                        }}>{p.checked_in_at ? 'Checked In' : p.rsvp_status}</span>
                        <span style={{ color: 'var(--rp-text-tertiary)' }}>{p.meetup_time ? formatDate(p.meetup_time) : 'No date'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailPanel;
